import React, { createContext, useContext, useEffect, useState } from "react";
import { getBrandingInfo } from "../services/branding.service";
import { CANONICAL_ORIGIN, isDevHostname } from "../hostRouting.tsx";
import defaultMainLogo from "../assets/images/moto-scheduler-main-logo.jpg";
import defaultIconLogo from "../assets/images/moto-scheduler-icon.jpg";
import defaultEmblemLogo from "../assets/images/moto-scheduler-emblem.jpg";

export const DEFAULT_MAIN_LOGO_URL = defaultMainLogo;
export const DEFAULT_ICON_URL = defaultIconLogo;
export const DEFAULT_EMBLEM_URL = defaultEmblemLogo;
export const DEFAULT_LOGO_URL = DEFAULT_MAIN_LOGO_URL;

export type BrandLogoVariant = "main" | "icon" | "emblem";

export const getDefaultLogoByVariant = (variant?: BrandLogoVariant) => {
  switch (variant) {
    case "icon":
      return DEFAULT_ICON_URL;
    case "emblem":
      return DEFAULT_EMBLEM_URL;
    case "main":
    default:
      return DEFAULT_MAIN_LOGO_URL;
  }
};

const BrandingContext = createContext<string | null>(null);

type BrandingProviderProps = {
  children: React.ReactNode;
};

export const BrandingProvider = ({ children }: BrandingProviderProps) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    getBrandingInfo().then((info) => {
      if (!isMounted) return;

      if (
        !info.exists &&
        typeof window !== "undefined" &&
        !isDevHostname(window.location.hostname)
      ) {
        window.location.replace(CANONICAL_ORIGIN);
        return;
      }

      if (info.logoUrl) {
        setLogoUrl(info.logoUrl);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <BrandingContext.Provider value={logoUrl}>
      {children}
    </BrandingContext.Provider>
  );
};

export type BrandLogoProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  variant?: BrandLogoVariant;
  fallbackSrc?: string;
};

const BrandLogo = ({
  variant = "main",
  fallbackSrc,
  src,
  alt = "",
  onError,
  ...props
}: BrandLogoProps) => {
  const tenantLogoUrl = useContext(BrandingContext);
  const defaultFallback = fallbackSrc || getDefaultLogoByVariant(variant);
  const activeLogoUrl = src || tenantLogoUrl || defaultFallback;

  return (
    <img
      {...props}
      alt={alt}
      src={activeLogoUrl}
      onError={(event) => {
        if (event.currentTarget.src !== defaultFallback) {
          event.currentTarget.src = defaultFallback;
        }
        onError?.(event);
      }}
    />
  );
};

export default BrandLogo;
